<template>
  <div class="overflow-hidden relative">
    <div
      v-tippy="{ content: 'Customer\'s Journey' }"
      class="flex items-center border border-secondary px-4 py-2 rounded"
    >
      <Icon type="car" />
      <div class="flex items-center justify-between ml-2 w-full text-sm gap-2">
        <div
          v-if="duration"
          class="flex items-center"
        >
          <div
            :class="duration.value < 1200 ? 'text-success' : 'text-warning'"
            class="font-bold"
          >
            {{ duration.text }}
          </div>
          <div class="text-quaternary text-xs ml-2 leading-3">{{ distance.text }}</div>
        </div>
        <div
          v-else-if="isLoadingDirections"
          class="text-quaternary text-xs"
        >
          Loading journey...
        </div>
        <div
          v-else-if="routeUnavailable"
          class="text-quaternary text-xs"
        >
          Route unavailable
        </div>
        <button
          v-if="canShowMap"
          type="button"
          class="text-xs text-secondary hover:text-primary ml-auto whitespace-nowrap"
          @click="toggleMap"
        >
          {{ mapVisible ? 'Hide map' : 'Show map' }}
        </button>
      </div>
    </div>
    <div
      v-show="mapVisible"
      class="mt-2 rounded-lg overflow-hidden relative"
      style="height: 180px"
    >
      <GoogleMap
        v-if="mapHasLoaded && from && to"
        ref="mapRef"
        :disable-default-ui="mapOptions.disableDefaultUi"
        :zoom-control="mapOptions.zoomControl"
        :styles="mapOptions.styles"
        api-key="AIzaSyCZHkYPG75ygU9wbygdd7O3Z2tXQF2oF-8"
        style="width: 100%; height: 100%"
        :center="center"
        :zoom="15"
        :libraries="['geometry']"
      >
        <Marker
          v-if="fromMarkerOptions.position"
          :options="fromMarkerOptions"
        />
        <Marker
          v-if="toMarkerOptions.position"
          :options="toMarkerOptions"
        />
        <Polyline
          v-if="polylineOptions.path.length"
          :options="polylineOptions"
        />
      </GoogleMap>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { GoogleMap, Marker, Polyline } from 'vue3-google-map'
import Icon from '@/components/icon/Icon.vue'
import useApiRequest from '@/composables/useApiRequest'

import { useUserSettingsStore } from '@/stores/UserSettingsStore'

const props = defineProps({
  from: {
    type: String,
    default: null,
    required: true
  },
  to: {
    type: String,
    default: null,
    required: true
  },
  coordinates: {
    type: Object,
    default: () => ({
      start: {
        lat: 0,
        lng: 0
      },
      end: {
        lat: 0,
        lng: 0
      }
    })
  }
})
const emit = defineEmits(['update:coordinates'])
const mapRef = ref(null)
const directionsRequest = useApiRequest()

const userSettingsStore = useUserSettingsStore()

/** Local route coordinates derived from Google Directions API. */
const routeCoordinates = ref({
  start: {
    lat: 0,
    lng: 0
  },
  end: {
    lat: 0,
    lng: 0
  }
})

let mapStyling = [
  {
    elementType: 'geometry',
    stylers: [
      {
        color: '#f5f5f5'
      }
    ]
  },
  {
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#616161'
      }
    ]
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [
      {
        color: '#f5f5f5'
      }
    ]
  },
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'administrative.land_parcel',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#bdbdbd'
      }
    ]
  },
  {
    featureType: 'administrative.neighborhood',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [
      {
        color: '#eeeeee'
      }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'labels.text',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#757575'
      }
    ]
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [
      {
        color: '#e5e5e5'
      }
    ]
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#9e9e9e'
      }
    ]
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [
      {
        color: '#ffffff'
      }
    ]
  },
  {
    featureType: 'road',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'road',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#757575'
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [
      {
        color: '#dadada'
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#616161'
      }
    ]
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#9e9e9e'
      }
    ]
  },
  {
    featureType: 'transit',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'transit.line',
    elementType: 'geometry',
    stylers: [
      {
        color: '#e5e5e5'
      }
    ]
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [
      {
        color: '#eeeeee'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      {
        color: '#caebf4'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'labels.text',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#9e9e9e'
      }
    ]
  }
]

// Determine may style based on userSettingsStore.userSettings.darkmode
if (userSettingsStore.userSettings.darkmode === 'dark') {
  mapStyling = [
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#212121'
        }
      ]
    },
    {
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off'
        }
      ]
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575'
        }
      ]
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#212121'
        }
      ]
    },
    {
      featureType: 'administrative',
      elementType: 'geometry',
      stylers: [
        {
          color: '#757575'
        }
      ]
    },
    {
      featureType: 'administrative.country',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e'
        }
      ]
    },
    {
      featureType: 'administrative.land_parcel',
      stylers: [
        {
          visibility: 'off'
        }
      ]
    },
    {
      featureType: 'administrative.locality',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#bdbdbd'
        }
      ]
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575'
        }
      ]
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          color: '#181818'
        }
      ]
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161'
        }
      ]
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#1b1b1b'
        }
      ]
    },
    {
      featureType: 'road',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#2c2c2c'
        }
      ]
    },
    {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#8a8a8a'
        }
      ]
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry',
      stylers: [
        {
          color: '#373737'
        }
      ]
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#3c3c3c'
        }
      ]
    },
    {
      featureType: 'road.highway.controlled_access',
      elementType: 'geometry',
      stylers: [
        {
          color: '#4e4e4e'
        }
      ]
    },
    {
      featureType: 'road.local',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161'
        }
      ]
    },
    {
      featureType: 'transit',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575'
        }
      ]
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#000000'
        }
      ]
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#3d3d3d'
        }
      ]
    }
  ]
}

const mapOptions = {
  disableDefaultUi: true,
  // zoomControl: true,
  styles: mapStyling
}

// reference variables
const center = ref({ lat: 0, lng: 0 })
const fromMarkerOptions = ref({ position: center })
const toMarkerOptions = ref({ position: center })

const polylineOptions = ref({
  path: [],
  strokeColor: '#5bc0de',
  strokeOpacity: 1,
  strokeWeight: 5,
  geodesic: true
})

const distance = ref(null)
const duration = ref(null)
const routeLegs = ref(null)
const encodedPolyline = ref('')
const routeUnavailable = ref(false)
const mapVisible = ref(false)
const mapHasLoaded = ref(false)
const isLoadingDirections = directionsRequest.isLoading
let directionsRequestToken = 0

const canShowMap = computed(() => !!routeLegs.value && !!encodedPolyline.value)

const toggleMap = () => {
  if (!mapVisible.value) {
    mapHasLoaded.value = true
    mapVisible.value = true
    return
  }
  mapVisible.value = false
}

const resetRoute = () => {
  routeLegs.value = null
  encodedPolyline.value = ''
  distance.value = null
  duration.value = null
  routeUnavailable.value = false
  polylineOptions.value = {
    ...polylineOptions.value,
    path: []
  }
}

const applyRouteToMap = () => {
  if (!mapRef.value?.api || !mapRef.value?.map || !routeLegs.value || !encodedPolyline.value) {
    return
  }

  let decodedPath = mapRef.value.api.geometry.encoding.decodePath(encodedPolyline.value)
  decodedPath = decodedPath.map(coord => ({
    lat: coord.lat(),
    lng: coord.lng()
  }))

  polylineOptions.value.path = decodedPath
  setMarkers(routeLegs.value)
}

const getDirections = () => {
  if (!props.from || !props.to) {
    return
  }

  const requestToken = ++directionsRequestToken
  resetRoute()

  directionsRequest
    .send({
      method: 'GET',
      endpoint: `/v1/utilities/googlemaps/directions?from=${encodeURIComponent(props.from)}&to=${encodeURIComponent(props.to)}`
    })
    .then(response => {
      if (requestToken !== directionsRequestToken) {
        return
      }

      const legs = response.data?.routes?.[0]?.legs?.[0]
      const overviewPolyline = response.data?.routes?.[0]?.overview_polyline?.points

      if (!response.success || !legs || !overviewPolyline) {
        routeUnavailable.value = true
        return
      }

      routeCoordinates.value.start = legs.start_location
      routeCoordinates.value.end = legs.end_location
      if (routeCoordinates.value.start.lat) {
        emit('update:coordinates', routeCoordinates.value)
      }

      routeLegs.value = legs
      encodedPolyline.value = overviewPolyline
      distance.value = legs.distance
      duration.value = legs.duration
      applyRouteToMap()
    })
    .catch(error => {
      if (requestToken !== directionsRequestToken) {
        return
      }
      console.error('Error fetching directions:', error)
      routeUnavailable.value = true
    })
}

const setMarkers = legs => {
  fromMarkerOptions.value = {
    position: { lat: legs.start_location.lat, lng: legs.start_location.lng },
    title: 'Customer',
    icon: {
      url: import.meta.env.VITE_CDN_BASE_URL + '/img/map-markers/customer.svg',
      scaledSize: new mapRef.value.api.Size(30, 30)
    }
  }
  toMarkerOptions.value = {
    position: { lat: legs.end_location.lat, lng: legs.end_location.lng },
    title: 'Dealership',
    icon: {
      url: import.meta.env.VITE_CDN_BASE_URL + '/img/map-markers/dealership.svg',
      scaledSize: new mapRef.value.api.Size(30, 30)
    }
  }

  const bounds = new mapRef.value.api.LatLngBounds()
  bounds.extend(fromMarkerOptions.value.position)
  bounds.extend(toMarkerOptions.value.position)
  mapRef.value.map.fitBounds(bounds)
}

watch(
  () => [props.from, props.to],
  () => {
    getDirections()
  },
  { immediate: true }
)

watch(
  () => mapRef.value?.ready,
  ready => {
    if (ready) {
      applyRouteToMap()
    }
  }
)
</script>

<style></style>
